import { Project } from "konect-model";
import { cloneDeep } from "lodash";
import EventService from "./event-service";
import ProjectService from "./project-service";
import { NavState } from "../view/App";
import { InfoPopupProps } from "../view/components/InfoPopup";

export enum UserControlEvent {
    UNDO_AVAILABLE = "UNDO_AVAILABLE",
    REDO_AVAILABLE = "REDO_AVAILABLE",
    UNDO = "UNDO",
    REDO = "REDO",
    SAVE_AVAILABLE = "SAVE_AVAILABLE",
    IS_SAVING = "IS_SAVING",
    BEFORE_SAVING = "BEFORE_SAVING",
    SHOW_INFO_POPUP = "SHOW_INFO_POPUP"
}

export default class UserControlService {
    //Class for handling undo/redo and save functionalities

    private static MAX_STATE_SIZE = 10;

    private static disabled = false;

    private static undoStates: Project[] = [];

    private static redoStates: Project[] = [];

    private static project: Project;

    private static onChange: (project: Project) => void;

    private static setView: (state: NavState) => void;

    private static eventBus: EventService = new EventService();

    static initialize(project: Project, onChange: (project: Project) => void, setView: (state: NavState) => void) {
        this.project = project;
        this.onChange = onChange;
        this.setView = setView;
    }

    static dispatch(event: UserControlEvent, args?: any) {
        this.eventBus.dispatch(event, args);
    }

    static disable(clearHistory?: boolean) {
        this.disabled = true;
        if (clearHistory) {
            this.undoStates = [];
            this.redoStates = [];
        }
        this.eventBus.dispatch(UserControlEvent.UNDO_AVAILABLE, false);
        this.eventBus.dispatch(UserControlEvent.REDO_AVAILABLE, false);
    }

    static enable() {
        this.disabled = false;
        this.eventBus.dispatch(UserControlEvent.UNDO_AVAILABLE, false);
        this.eventBus.dispatch(UserControlEvent.REDO_AVAILABLE, false);
    }

    static changeView(state: NavState) {
        this.setView(state);
    }

    static reset(project: Project) {
        this.project = project;
        this.undoStates = [];
        this.redoStates = [];
        this.eventBus.dispatch(UserControlEvent.UNDO_AVAILABLE, false);
        this.eventBus.dispatch(UserControlEvent.REDO_AVAILABLE, false);
        this.eventBus.dispatch(UserControlEvent.SAVE_AVAILABLE, false);
    }

    static async save() {
        this.eventBus.dispatch(UserControlEvent.BEFORE_SAVING);
        this.eventBus.dispatch(UserControlEvent.SAVE_AVAILABLE, false);
        this.eventBus.dispatch(UserControlEvent.IS_SAVING, true);
        const response = await ProjectService.saveProject(this.project);
        this.eventBus.dispatch(UserControlEvent.IS_SAVING, false);
        if (!response) {
            this.eventBus.dispatch(UserControlEvent.SAVE_AVAILABLE, true);
        }
    }

    static undo() {
        this.eventBus.dispatch(UserControlEvent.UNDO);
        if (this.disabled || this.undoStates.length <= 0) {
            return;
        }
        this.redoStates.push(cloneDeep(this.project));
        const prev = this.undoStates.pop();
        if (prev) {
            this.project = prev;
            this.onChange(prev);
        }
        if (this.undoStates.length <= 0) {
            this.eventBus.dispatch(UserControlEvent.UNDO_AVAILABLE, false);
        }
        this.eventBus.dispatch(UserControlEvent.REDO_AVAILABLE, true);
        this.eventBus.dispatch(UserControlEvent.SAVE_AVAILABLE, true);
    }

    static redo() {
        this.eventBus.dispatch(UserControlEvent.REDO);
        if (this.disabled || this.redoStates.length <= 0) {
            return;
        }
        this.undoStates.push(cloneDeep(this.project));
        const next = this.redoStates.pop();
        if (next) {
            this.project = next;
            this.onChange(next);
        }
        if (this.redoStates.length <= 0) {
            this.eventBus.dispatch(UserControlEvent.REDO_AVAILABLE, false);
        }
        this.eventBus.dispatch(UserControlEvent.UNDO_AVAILABLE, true);
        this.eventBus.dispatch(UserControlEvent.SAVE_AVAILABLE, true);
    }

    static update() {
        if (this.disabled) {
            return;
        }
        this.undoStates = [...this.undoStates, cloneDeep(this.project)];
        if (this.undoStates.length === 1) {
            this.eventBus.dispatch(UserControlEvent.UNDO_AVAILABLE, true);
        }
        if (this.redoStates.length >= 1) {
            this.redoStates = [];
            this.eventBus.dispatch(UserControlEvent.REDO_AVAILABLE, false);
        }
        if (this.undoStates.length > this.MAX_STATE_SIZE) {
            this.undoStates.shift();
        }
        this.eventBus.dispatch(UserControlEvent.SAVE_AVAILABLE, true);
    }

    static showInfoPopup(props: InfoPopupProps) {
        this.eventBus.dispatch(UserControlEvent.SHOW_INFO_POPUP, props);
    }

    static onUndoAvailable(callback: Function) {
        this.eventBus.on(UserControlEvent.UNDO_AVAILABLE, callback);
    }

    static onRedoAvailable(callback: Function) {
        this.eventBus.on(UserControlEvent.REDO_AVAILABLE, callback);
    }

    static onSaveAvailable(callback: Function) {
        this.eventBus.on(UserControlEvent.SAVE_AVAILABLE, callback);
    }

    static onSaving(callback: Function) {
        this.eventBus.on(UserControlEvent.IS_SAVING, callback);
    }

    static onShowInfoPopup(callback: Function) {
        this.eventBus.on(UserControlEvent.SHOW_INFO_POPUP, callback);
    }

    static on(event: UserControlEvent, callback: Function, replace?: string) {
        return this.eventBus.on(event, callback, replace);
    }

    static canUndo() {
        return this.undoStates.length > 0;
    }

    static canRedo() {
        return this.redoStates.length > 0;
    }

}